<template>
  <div v-if="$route.query.view === 'print'">
    <LazyPropertyPDF :property="property" />
  </div>
  <div v-else class="ucrs-property">
    <section>
      <div class="container">
        <div class="flex items-center justify-between h-22 lg:h-auto">
          <Breadcrumbs
            :items="links.breadcrumbs.nav.items"
            label="label"
            url="url"
            class="lg:flex-1 z-10 md:z-0 max-w-[30%] md:max-w-full"
          />
          <PrevNext :id="id" class="flex lg:hidden" />
          <SvgIcon
            name="share"
            role="button"
            tabindex="0"
            aria-label="Apri menù condivisione"
            :aria-expanded="showShare"
            :aria-controls="`ShareRegion-${_uid}`"
            class="max-w-9.6 max-h-11 cursor-pointer z-10 md:z-0 outline-none focus-visible:ring-2 focus-visible:rounded focus-visible:ring-blue"
            @keyup.enter="showShare = !showShare"
            @click="showShare = !showShare"
          />
          <PrevNext :id="id" class="hidden lg:flex w-1/3 pl-6.4 ml-6.4" />
        </div>
      </div>
    </section>
    <ShareAccordion :region-id="`ShareRegion-${_uid}`" :show="showShare" />
    <section>
      <div class="container">
        <div class="lg:flex lg:flex-wrap lg:-mx-6.4">
          <div class="lg:w-2/3 lg:px-6.4">
            <ClientOnly>
              <PropertyGallery class="-mx-6 lg:mx-0" :property="property" />
              <template slot="placeholder">
                <div
                  class="w-full h-[225px] sm:h-[390px] md:h-[443px] border-gray-lightest animate-pulse"
                >
                  <OSkeleton
                    item-class="h-[225px] sm:h-[390px] md:h-[443px]"
                    root-class="h-[225px] sm:h-[390px] md:h-[443px]"
                    :rounded="false"
                  ></OSkeleton>
                </div>
              </template>
            </ClientOnly>
            <div class="lg:flex lg:flex-col">
              <PropertyFeatures
                class="hidden lg:flex"
                :property="property"
                :features="features"
                @hidden-add="$set(property, 'is_hidden', true)"
                @hidden-remove="$set(property, 'is_hidden', false)"
                @favorite-add="$set(property, 'is_favorite', true)"
                @favorite-remove="$set(property, 'is_favorite', false)"
              />
              <h1
                v-dompurify-html="property.title"
                class="ucrs-property__title"
              />
              <div
                class="mt-8 mb-6 lg:mb-0 lg:flex lg:justify-between space-y-8 lg:space-y-0 lg:space-x-4"
              >
                <p
                  v-if="property.location.locality.thoroughfare.show_address"
                  class="ucrs-property__address ucrs-property__address--large"
                  :class="{
                    'text-blue underline hover:no-underline cursor-pointer':
                      property.location.locality.show_map,
                  }"
                  :tabindex="property.location.locality.show_map ? 0 : -1"
                  role="button"
                  :aria-label="
                    property.location.locality.show_map ? 'Mostra mappa' : ''
                  "
                  @click="property.location.locality.show_map && goToMap()"
                  @keyup.enter="property.location.locality.show_map && goToMap()"
                >
                  <SvgIcon
                    :name="
                      property.location.locality.show_map
                        ? 'marker-blue'
                        : 'marker'
                    "
                    class="w-8 h-11 mr-5"
                  />
                  <span
                    v-dompurify-html="
                      `${property.location.locality.thoroughfare.address}${
                        property.location.locality.thoroughfare
                          .show_address_number
                          ? ' ' +
                            property.location.locality.thoroughfare
                              .address_number
                          : ''
                      }`
                    "
                    class="first-letter:uppercase"
                  ></span>
                </p>
                <p
                  class="ucrs-property__price"
                  v-text="propertyPrice(property)"
                ></p>
              </div>
              <PropertyFeatures
                class="block lg:hidden"
                :property="property"
                :features="features"
                @hidden-add="$set(property, 'is_hidden', true)"
                @hidden-remove="$set(property, 'is_hidden', false)"
                @favorite-add="$set(property, 'is_favorite', true)"
                @favorite-remove="$set(property, 'is_favorite', false)"
              />
              <LazyPropertyActions
                :property="property"
                class="mb-6 lg:hidden"
                icon-size="small"
                @hidden-add="$set(property, 'is_hidden', true)"
                @hidden-remove="$set(property, 'is_hidden', false)"
                @favorite-add="$set(property, 'is_favorite', true)"
                @favorite-remove="$set(property, 'is_favorite', false)"
              />
            </div>
            <div ref="propertyTabs">
              <UcrsTabs v-model="activeTab" @input="onTabsInput">
                <UcrsTabItem
                  v-if="property.building.category.name === 'Cantieri'"
                  key="units"
                  value="units"
                  label="Tipologie"
                >
                  <PropertyUnits :property="property" />
                </UcrsTabItem>
                <UcrsTabItem
                  key="description"
                  value="description"
                  label="Descrizione"
                >
                  <p
                    v-dompurify-html="
                      translation ? translation : property.description
                    "
                  ></p>
                  <div class="md:flex md:flex-wrap md:justify-between mb-16">
                    <div class="flex items-center mt-6 mb-11 md:mt-10 md:mb-0">
                      <p class="flex items-center mr-5">
                        <span class="whitespace-nowrap">Power by</span>
                        <NuxtImg
                          src="/assets/img/logo-google.svg"
                          alt="Google logo"
                          width="58"
                          height="20"
                          class="mx-2"
                        />
                        Traduttore:
                      </p>
                      <DropdownWithValidation
                        :value="language"
                        :options="languages"
                        placeholder="Lingua"
                        classes="ucrs-input--focus"
                        field="text"
                        name="google-translate"
                        aria-label="Traduci la descrizione"
                        field-classes="w-40 sm:w-48 md:w-56"
                        @select="translate"
                      />
                    </div>
                    <div v-if="partner" class="flex items-center mt-6 md:mt-10">
                      <p class="flex items-center">
                        Immobile proveniente da
                        <NuxtImg
                          class="max-w-48 max-h-16 mx-3"
                          :src="partner.img"
                          :alt="`Logo ${partner.name}`"
                        />
                      </p>
                      <Tooltip>
                        <p slot="content" v-dompurify-html="partner.text"></p>
                        <p slot="trigger">
                          <SvgIcon name="info" class="w-9 h-9"></SvgIcon>
                        </p>
                      </Tooltip>
                    </div>
                  </div>
                  <h2 class="font-bold text-xl mb-8">
                    Caratteristiche
                    {{
                      property.building.category.name === 'Cantieri'
                        ? 'del cantiere'
                        : property.building.category.name === 'terreni'
                        ? 'del terreno'
                        : "dell'immobile"
                    }}
                  </h2>
                  <LazyPropertyTable :property="property" />
                  <p class="my-10">
                    <a
                      id="linkPdf"
                      class="flex items-center text-blue text-lg font-bold outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded focus-visible:ring-blue"
                      href="javascript:void(0)"
                      @click="onPrintClick"
                    >
                      <SvgIcon name="print" class="max-w-9.6 max-h-9.6 mr-4" />
                      Stampa scheda annuncio
                    </a>
                  </p>
                  <PropertyValue :home-value="property.home_value" />
                </UcrsTabItem>
                <UcrsTabItem
                  v-if="property.floor_plans.length"
                  key="floor_plans"
                  value="floor_plans"
                  label="Planimetrie"
                >
                  <LazyPropertyFloorplans :property="property" />
                </UcrsTabItem>
                <UcrsTabItem
                  v-if="property.location.locality.show_map"
                  key="map"
                  value="map"
                  label="Mappa"
                >
                  <LazyPropertyMap :property="property" />
                </UcrsTabItem>
              </UcrsTabs>
            </div>
          </div>
          <div class="hidden lg:block lg:w-1/3 lg:px-6.4">
            <LazyPropertyForm
              :sticky="true"
              :property="property"
              :user-is-owner="userIsOwner"
              @submitted="handleFormSubmit"
              @favorite-add="$set(property, 'is_favorite', true)"
            />
          </div>
        </div>
        <StripBanner
          link="/servizi-documenti-casa"
          :full="false"
          :no-icon="true"
          bg-color="bg-white-dark"
          btn-text="Scopri i servizi"
          root-class="-mx-6 md:mx-0 md:mb-16 my-10"
        >
          <div>
            <h3 class="mb-3">Servizi immobiliari</h3>
            <p class="font-normal normal-case">
              C'è un <strong>partner</strong> per ogni bisogno, gestire la
              compravendita, alleggerire la bolletta, cercare il documento che
              ti manca.
            </p>
          </div>
        </StripBanner>
        <LazyPropertyRelated
          v-if="property.building.category.name !== 'Cantieri'"
          :property="property"
          class="mt-16"
        />
        <p
          v-if="property.agent && property.agent.type === 'proprieta_unicredit'"
          class="mt-20 md:mt-28 text-xs font-sans"
        >
          Note:<br aria-hidden="true" />
          (1) UniCredit RE Services S.p.A. comunica di essere una società
          controllata da UniCredit S.p.A.<br aria-hidden="true" />
          In considerazione di tale circostanza, UniCredit RE Services potrebbe
          trovarsi in una situazione di conflitto di interessi in quanto può
          essere coinvolta in attività di intermediazione immobiliare aventi ad
          oggetto immobili di proprietà del Gruppo UniCredit.
        </p>
        <MetaLinks
          v-if="links.breadcrumbs.city && links.breadcrumbs.city.items.length"
          :header="links.breadcrumbs.city.heading"
          :links="links.breadcrumbs.city.items"
          label="label"
          url="url"
          show-on-close="8"
          class="mt-11 md:mt-28"
        />
        <MetaLinks
          v-if="
            links.breadcrumbs.sub_administrative_area &&
            links.breadcrumbs.sub_administrative_area.items.length
          "
          :header="links.breadcrumbs.sub_administrative_area.heading"
          :links="links.breadcrumbs.sub_administrative_area.items"
          label="label"
          url="url"
          show-on-close="9"
          class="mt-14 md:mt-11"
        />
      </div>
    </section>
    <StickyCta v-if="!userIsOwner" @click="clickStickyCta">
      {{
        $store.state.auth.loggedIn ? 'Voglio essere ricontattato' : 'Contattaci'
      }}
    </StickyCta>
  </div>
</template>

<script>
// Libs
import pick from 'lodash/pick'
import { defineAsyncComponent } from 'vue'

import { features, partner } from '~/assets/js/computed'
import { propertyPrice } from '~/assets/js/methods'

import Breadcrumbs from '~/components/Breadcrumbs.vue'
import DropdownWithValidation from '~/components/DropdownWithValidation.vue'
import MetaLinks from '~/components/MetaLinks.vue'
import PrevNext from '~/components/PrevNext.vue'
import PropertyGallery from '~/components/PropertyGallery.vue'
import PropertyFeatures from '~/components/PropertyFeatures.vue'
import PropertyUnits from '~/components/PropertyUnits.vue'
import PropertyValue from '~/components/PropertyValue.vue'
import ShareAccordion from '~/components/ShareAccordion.vue'
import StickyCta from '~/components/StickyCta.vue'
import Tooltip from '~/components/Tooltip.vue'
import UcrsTabs from '~/components/UcrsTabs.vue'
import UcrsTabItem from '~/components/UcrsTabItem'
import StripBanner from '~/components/StripBanner.vue'

const LazyPropertyMap = () => import('~/components/PropertyMap')
const LazyModalInner = () => import('~/components/ModalInner')
const LazyModalResponse = () => import('~/components/ModalResponse')

// TODO: test if lazyness is working and necessary
const LazyPropertyActions = defineAsyncComponent(() =>
  import('~/components/PropertyActions.vue')
)
const LazyPropertyFloorplans = defineAsyncComponent(() =>
  import('~/components/PropertyFloorplans.vue')
)
const LazyPropertyForm = defineAsyncComponent(() =>
  import('~/components/PropertyForm.vue')
)
const LazyPropertyPDF = defineAsyncComponent(() =>
  import('~/components/PropertyPDF.vue')
)
const LazyPropertyRelated = defineAsyncComponent(() =>
  import('~/components/PropertyRelated.vue')
)
const LazyPropertyTable = defineAsyncComponent(() =>
  import('~/components/PropertyTable.vue')
)

export default {
  components: {
    StripBanner,
    Breadcrumbs,
    DropdownWithValidation,
    MetaLinks,
    PrevNext,
    PropertyGallery,
    PropertyFeatures,
    PropertyUnits,
    PropertyValue,
    ShareAccordion,
    StickyCta,
    Tooltip,
    UcrsTabs,
    UcrsTabItem,
    LazyPropertyActions,
    LazyPropertyFloorplans,
    LazyPropertyForm,
    LazyPropertyPDF,
    LazyPropertyRelated,
    LazyPropertyTable,
    LazyPropertyMap,
  },

  layout({ query }) {
    return query.view === 'print' ? 'empty' : 'default'
  },

  async asyncData({ route, $axios, params, error, store, redirect }) {
    try {
      const endpoint =
        params.section === 'nuovecostruzioni'
          ? `/_api/api/v2/constructions/${params.id}`
          : `/_api/api/v2/properties/${params.id}`
      const res = await $axios.get(endpoint)

      if (!res.data) {
        throw new Error('ID not found')
      }

      const url = res.data.data.url

      if (url !== route.path) {
        return redirect(301, url)
      }

      store.commit('setFooterBottomSpaced', true)

      if (!store.state.auth.loggedIn) {
        return {
          id: params.id,
          property: res.data.data,
          meta: res.data.meta,
          links: res.data.extra,
        }
      }

      const sellerPropertiesRes = await $axios.get(
        '/_api/api/v2/seller/properties'
      )
      const sellerProperties = sellerPropertiesRes.data.data

      return {
        id: params.id,
        property: res.data.data,
        meta: res.data.meta,
        links: res.data.extra,
        sellerProperties,
      }
    } catch (e) {
      error({ statusCode: 404 })
    }
  },

  data() {
    return {
      showShare: false,
      activeTab: 'description',
      translation: '',
      language: null,
      languages: [
        { value: 'it', text: 'Italiano' },
        { value: 'en', text: 'Inglese' },
        { value: 'fr', text: 'Francese' },
        { value: 'de', text: 'Tedesco' },
        { value: 'es', text: 'Spagnolo' },
        { value: 'zh-TW', text: 'Cinese' },
        { value: 'ru', text: 'Russo' },
        { value: 'ar', text: 'Arabo' },
      ],
      response: {
        icon: 'check',
        color: 'white',
        width: '83rem',
        text: ``,
      },
      units: [],
      sellerProperties: null,
      tealiumPayload: null,
    }
  },

  head() {
    const head = {
      // TMP
      ...(this.meta
        ? {
            title: this.meta.title,
            meta: [
              {
                hid: 'description',
                name: 'description',
                content: this.meta.description,
              },
              {
                hid: 'og:title',
                property: 'og:title',
                content: this.meta.title,
              },
              {
                hid: 'og:description',
                property: 'og:description',
                content: this.meta.description,
              },
              {
                hid: 'og:url',
                property: 'og:url',
                content: this.$config.baseURL + this.property.url,
              },
              ...(this.property.images?.length
                ? [
                    {
                      hid: 'og:image',
                      property: 'og:image',
                      content: this.property.images[0].src,
                    },
                  ]
                : []),
            ],
          }
        : {}),
      script: [
        { type: 'application/ld+json', json: this.breadcrumbsSchema },
        { type: 'application/ld+json', json: this.productSchema },
      ],
    }

    return head
  },

  computed: {
    features,
    partner,

    userIsOwner() {
      const { sellerProperties } = this

      if (!sellerProperties?.length) {
        return
      }

      const userIsOwner = sellerProperties.some(
        (it) => it.id === this.property.id
      )

      return userIsOwner
    },
    breadcrumbsSchema() {
      const schema = {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [],
      }
      this.links.breadcrumbs.nav.items.forEach((item, i) => {
        schema.itemListElement.push({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@id': this.$config.baseURL + item.url,
            name: item.label,
          },
        })
      })
      return schema
    },
    productSchema() {
      const schema = {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name: this.property.title,
        description: this.property.description,
        category: this.property.building.property_type.name
          ? this.property.building.property_type.name
          : this.property.building.category.name,
        url: this.$config.baseURL + this.property.url,
        brand: 'UniCredit RE Services',
        offers: {
          '@type': 'Offer',
          price: this.propertyPrice(this.property),
          priceCurrency: this.property.transaction.currency,
        },
        ...(this.property.images?.length
          ? {
              image: this.property.images[0].src,
            }
          : {}),
      }

      return schema
    },
  },
  mounted() {
    const propertyAlreadyPresent = this.$store.getters[
      'properties/getPropertyById'
    ](this.property.id)

    if (!propertyAlreadyPresent) {
      this.$store.dispatch('properties/add', {
        id: this.property.id,
        url: this.property.url,
        title: this.property.title,
        image: {
          src: this.property.images[0].src,
        },
        features: {
          surface: this.property.features.surface,
          rooms: this.property.features.rooms,
          land_morphology: this.property.features.land_morphology,
          surface_min: this.property.features.surface_min,
          rooms_min: this.property.features.rooms_min,
          virtual_tour: this.property.features.virtual_tour,
          surface_unit: this.property.features.surface_unit,
        },
        building: {
          is_luxury: this.property.building.is_luxury,
          category: {
            name: this.property.building.category.name,
          },
        },
        location: {
          city: this.property.location.city,
          locality: {
            neighbourhood: this.property.location.locality.neighbourhood,
          },
        },
        transaction: {
          ...(this.property.transaction.price_upon_request
            ? {}
            : { price: this.property.transaction.price }),
          price_min: this.property.transaction.price_min,
          price_upon_request: this.property.transaction.price_upon_request,
        },
      })
    }

    const tealiumPayload = {
      annuncio_categoria: this.$utilities.tealiumCategory(this.property),
      annuncio_vtour: this.property.features.virtual_tour ? '1' : '0',
      annuncio_vetrina: this.property.is_showcase ? '1' : '0',
      annuncio_certificato: this.property.certificate ? '1' : '0',
      annuncio_network:
        this.property.agent && this.property.agent.type === 'network_agenti'
          ? '1'
          : '0',
      annuncio_mappa: this.property.location.locality.show_map ? '1' : '0',
      annuncio_prezzo: this.propertyPrice(
        this.property,
        true,
        'Trattativa ris.'
      ),
      annuncio_num_locali: this.property.features.rooms
        ? this.property.features.rooms
        : this.property.features.rooms_min
        ? this.property.features.rooms_min
        : 0,
      annuncio_via: this.property.location.locality.thoroughfare.show_address
        ? `${this.property.location.locality.thoroughfare.address}${
            this.property.location.locality.thoroughfare.show_address_number
              ? ' ' +
                this.property.location.locality.thoroughfare.address_number
              : ''
          }`
        : '',
      annuncio_report: this.property.has_home_value_report ? 'SI' : 'NO',
    }
    if (this.$config.uclamActive) {
      tealiumPayload.annuncio_unicredit =
        // eslint-disable-next-line eqeqeq
        this.property.source_id == 4 ? '1' : '0'
    }
    if (this.property.features.energy_class)
      tealiumPayload.annuncio_classe_energetica = this.property.features.energy_class

    this.tealiumPayload = tealiumPayload
    this.$tealium.pageTrack(tealiumPayload, true)
  },

  methods: {
    propertyPrice,

    async translate(lang) {
      if (!lang) {
        this.translation = ''
        return
      }

      this.language = lang

      const text = this.property.description

      const res = await this.$axios.post('_api/api/v1/translate', {
        lang: lang.value,
        text,
      })

      this.translation = res.data
    },

    goToMap() {
      this.$tealium.track({
        event_name: 'tab_click_scheda',
        scheda_tipo: 'Mappa',
        page_trigger: 'scheda_annuncio_tab',
        link_name: 'tab_click_scheda',
        event_type: 'tab_click_scheda',
      })
      this.activeTab = 'map'
    },

    onTabsInput(tab) {
      const tabLabel = {
        description: 'Descrizione',
        floor_plans: 'Planimetrie',
        map: 'Mappa',
      }

      this.$tealium.track({
        event_name: 'tab_click_scheda',
        scheda_tipo: tabLabel[tab],
        page_trigger: 'scheda_annuncio_tab',
        link_name: 'tab_click_scheda',
        event_type: 'tab_click_scheda',
      })
    },

    clickStickyCta() {
      if (this.$store.state.auth.loggedIn) {
        this.$nuxt.$emit('submit-loggedin')
        return
      }

      this.$oruga.modal.open({
        parent: this,
        width: '83rem',
        animation: 'ucrs-modal-',
        component: LazyModalInner,
        props: {
          contentClass: 'py-32 md:py-48 mx-auto max-w-screen-sm',
          component: LazyPropertyForm,
          componentProps: {
            sticky: false,
            inModal: true,
            property: this.property,
          },
          componentEvents: {
            submitted: (res) => {
              this.handleFormSubmit(res)
            },
            'favorite-add': () => {
              this.property.is_favorite = true
            },
          },
        },
      })
    },

    handleFormSubmit(response) {
      this.$oruga.modal.open({
        parent: this,
        width: response.width,
        animation: 'ucrs-modal-',
        component: LazyModalInner,
        props: {
          bgColor: response.color,
          component: LazyModalResponse,
          componentProps: {
            icon: response.icon,
            iconClasses: response.iconClasses,
            content: response.text,
          },
        },
        onCancel: this.trackModalClose,
        events: {
          close: () => {
            this.trackModalClose()
          },
        },
      })
    },

    trackModalClose() {
      this.$tealium.track({
        event_name: 'chiusura_richiesta',
        id: this.property.id,
      })
    },

    handleFormSuccess() {
      const tealiumPayload = pick(this.tealiumPayload, [
        'id',
        'annuncio_categoria',
        'annuncio_vtour',
        'annuncio_vetrina',
        'annuncio_certificato',
        'annuncio_network',
        'annuncio_unicredit',
      ])

      this.$tealium.track({
        event_name: 'apertura_richiesta',
        id: this.property.id,
        ...tealiumPayload,
      })
    },

    async onPrintClick() {
      const { id } = this.property

      this.$tealium.track({ event_name: 'download_scheda' })

      try {
        // Utilizziamo $axios per garantire che tutti i cookie di sessione
        // e gli header di sicurezza siano inclusi nella richiesta
        const response = await this.$axios.get(`/pdf?id=${id}`, {
          responseType: 'blob',
          headers: {
            Accept: 'application/pdf',
          },
        })

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)

        // Creiamo un link temporaneo per forzare l'apertura in un nuovo tab
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'

        // Se preferissi il download diretto:
        // link.download = `Scheda-Immobile-${id}.pdf`

        link.click()

        // Memory management: rilasciamo l'oggetto dopo l'apertura
        setTimeout(() => window.URL.revokeObjectURL(url), 1000)
      } catch (error) {
        console.error('PDF Generation Error:', error)
        // Fallback se il download fallisce (es. 403 persistente)
        // Potrebbe essere necessario un feedback UI per l'utente
      }
    },
  },
}
</script>

<style lang="postcss">
@import '~/assets/css/components/property';
@import '~/assets/css/components/tabs';
</style>
